import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useParams } from "wouter";
import { getSocket } from "../socket.js";
import { getLegalTiles } from "../logicHelpers/legalTiles";
import { getOccupiedTiles } from "../gameLogic/helpers";
import { useGameHelpers } from "../stores/useGameHelpers.js";
import { useAnimationPlaying } from "./useAnimationPlaying";
import useSoundEffectsPlayer from "../soundPlayers/useSoundEffectsPlayer";
import * as THREE from "three";
import gsap from "gsap";
import {
	audioVolumeAtom,
	backdoLaunchAtom,
	clientAtom,
	gamePhaseAtom,
	hasTurnAtom,
	helperTilesAtom,
	legalTilesAtom,
	pauseGameAtom,
	selectionAtom,
	shortcutOptionsAtom,
	showFinishMovesAtom,
	teamsAtom,
	turnAtom,
} from "../GlobalState";

const socket = getSocket();

export default function useSelect({
	targetTile,
	canStartSelection,
	getSelectionData,
	isLegalMoveTarget,
}) {
	const [selection, setSelection] = useAtom(selectionAtom);
	const setLegalTiles = useSetAtom(legalTilesAtom);
	const setHelperTiles = useSetAtom(helperTilesAtom);
	const setShowFinishMoves = useSetAtom(showFinishMovesAtom);
	const teams = useAtomValue(teamsAtom);
	const turn = useAtomValue(turnAtom);
	const client = useAtomValue(clientAtom);
	const gamePhase = useAtomValue(gamePhaseAtom);
	const hasTurn = useAtomValue(hasTurnAtom);
	const paused = useAtomValue(pauseGameAtom);
	const backdoLaunch = useAtomValue(backdoLaunchAtom);
	const shortcutOptions = useAtomValue(shortcutOptionsAtom);
	const legalTilesState = useAtomValue(legalTilesAtom);
	const audioVolume = useAtomValue(audioVolumeAtom);
	const animationPlaying = useAnimationPlaying();
	const { playSoundEffect } = useSoundEffectsPlayer();
	const params = useParams();

	const setTokenChoices = useGameHelpers(s => s.setTokenChoices);
	const setTokenChoice = useGameHelpers(s => s.setTokenChoice);

	function playTileMoveAnimation(tile, moveInfo) {
		const tileAnimationRegistry = useGameHelpers.getState().tileAnimationRegistry;
		const tileAnimation = tileAnimationRegistry[tile] ?? tileAnimationRegistry[String(tile)]; // looks up by number and string
		if (!tileAnimation) return;

		const {
			meshGroupRef,
			groupRef,
			flashMaterialsRef,
			rollStar,
		} = tileAnimation;

		if (meshGroupRef?.current) {
			gsap.killTweensOf(meshGroupRef.current.scale);
			gsap
				.timeline()
				.to(meshGroupRef.current.scale, {
					x: 1.5,
					y: 1.5,
					z: 1.5,
					duration: 0.16,
					ease: "power2.out",
				})
				.to(meshGroupRef.current.scale, {
					x: 0.9,
					y: 0.9,
					z: 0.9,
					duration: 0.12,
					delay: 0.04,
					ease: "power1.out",
				})
				.to(meshGroupRef.current.scale, {
					x: 1,
					y: 1,
					z: 1,
					duration: 0.1,
					ease: "power1.out",
				});
		}

		if (groupRef?.current && rollStar) {
			const worldPos = new THREE.Vector3();
			groupRef.current.getWorldPosition(worldPos);
			const burstCount = 6;
			for (let i = 0; i < burstCount; i++) {
				const angle = (i / burstCount) * Math.PI * 2;
				rollStar({
					position: worldPos,
					rotation: new THREE.Vector3(0, angle, 0),
					omitFactor: 8,
					size: 0.0008,
					scale: 0.2 + Math.random() * 0.2,
					color: new THREE.Color().setHSL(0.13 + Math.random() * 0.05, 1, 0.6),
					duration: 1,
					speedX: 3 + Math.random() * 2,
					speedY: 1.5 + Math.random() * 1.5,
					brightness: 1.0,
				});
			}
		}

		const moveValue = Number(moveInfo?.move);
		const flashColor =
			moveValue === 4
				? new THREE.Color("#2558ff")
				: moveValue === 5
					? new THREE.Color("#6725ff")
					: null;

		if (!flashColor || !meshGroupRef?.current) return;
		if (tile === 0 || tile === 5 || tile === 10 || tile === 15 || tile === 22) return;

		if (flashMaterialsRef?.current) {
			flashMaterialsRef.current.forEach(m => gsap.killTweensOf(m));
			flashMaterialsRef.current = [];
		}

		meshGroupRef.current.traverse((child) => {
			if (child.isMesh && child.material) {
				const mat = child.material;
				const origColor = mat.color.clone();
				mat.color.copy(flashColor);
				if (flashMaterialsRef?.current) {
					flashMaterialsRef.current.push(mat);
				}
				gsap.killTweensOf(mat.color);
				gsap.to(mat.color, {
					r: origColor.r,
					g: origColor.g,
					b: origColor.b,
					duration: 0.4,
					ease: "power4.in",
				});
			}
		});
	}

	const handlePointerDown = (event) => {
    event.stopPropagation();

		if (!(gamePhase === "game" && hasTurn && !animationPlaying && !paused)) {
			return;
		}

		if (selection === null) {
			if (!canStartSelection({
				targetTile,
				selection,
				client,
				teams,
				legalTiles: legalTilesState,
			})) {
				return;
			}

			const selectionData = getSelectionData({
				targetTile,
				selection,
				client,
				teams,
				legalTiles: legalTilesState,
			});
			if (!selectionData) {
				return;
			}

			const { tile, pieces, history, team } = selectionData;
			const legalTiles = getLegalTiles(
				tile,
				teams[team].moves,
				teams[team].pieces,
				history,
				backdoLaunch,
				shortcutOptions,
			);

			if (Object.keys(legalTiles).length === 0) {
				return;
			}

			setSelection({ tile, pieces });
			setLegalTiles(legalTiles);

			const helperTiles = {};
			for (const legalTile of Object.keys(legalTiles)) {
				if (legalTile !== "29") {
					const moveInfo = legalTiles[legalTile];
					helperTiles[legalTile] = parseInt(moveInfo.move);
				}
			}
			setHelperTiles(helperTiles);

			const legalTilesSet = new Set();
			for (const key in legalTiles) {
				legalTilesSet.add(parseInt(key));
			}
			setTokenChoices(Array.from(legalTilesSet));
			setTokenChoice(0);

			socket.emit("select", {
				roomId: params.id.toUpperCase(),
				selection: { tile, pieces },
				legalTiles,
			});
			return;
		}

		if (selection.tile !== targetTile && isLegalMoveTarget({
			targetTile,
			selection,
			legalTiles: legalTilesState,
			teams,
			client,
		})) {
			const moveInfo = legalTilesState[targetTile] ?? legalTilesState[String(targetTile)];

			socket.emit("move", {
				roomId: params.id.toUpperCase(),
				tile: targetTile,
				playerName: client.name,
			});

			playSoundEffect("/sounds/effects/star-move.mp3", audioVolume);
			playTileMoveAnimation(targetTile, moveInfo);
			setTokenChoices([]);
			setTokenChoice(null);
			return;
		}

		setSelection(null);
		setLegalTiles({});
		setHelperTiles({});
		setShowFinishMoves(false);
		const occupiedTiles = getOccupiedTiles({ pieces: teams[turn.team].pieces });
		setTokenChoices(occupiedTiles);
		setTokenChoice(0);

		socket.emit("select", {
			roomId: params.id.toUpperCase(),
			selection: null,
			legalTiles: {},
		});
		playSoundEffect("/sounds/effects/deselect.mp3", audioVolume);
	};

	return handlePointerDown;
}
